import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { gql, useMutation } from '@apollo/client';
import type { Client } from '../../types/client';
import { CREATE_PACKAGE_CLIENT } from '../../graphql/admin/mutations/package-client';

interface PackageClient {
  id?: number;
  packageId: number;
  clientId: number;
  passport?: string;
}

interface PackageClientFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  packageId: number;
  client?: Client | null;
  packageClient?: PackageClient | null;
}

const UPDATE_PACKAGE_CLIENT = gql`
  mutation UpdatePackageClient($id: Int!, $input: PackageClientInput!) {
    updatePackageClient(id: $id, input: $input) {
      id
      packageId
      clientId
      passport
      client {
        id
        firstName
        lastName
        email
        phone
        agencyId
        agency {
          name
        }
      }
    }
  }
`;

const CREATE_CLIENT = gql`
  mutation CreateClient($input: ClientInput!) {
    createClient(input: $input) {
      id
      firstName
      lastName
      email
      phone
      agencyId
    }
  }
`;

export default function PackageClientFormModal({ 
  isOpen, 
  onClose, 
  packageId,
  client: existingClient,
  packageClient: existingPackageClient
}: PackageClientFormModalProps) {
  const [formData, setFormData] = useState<Client>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    agencyId: 0
  });

  const [passportFile, setPassportFile] = useState<File | null>(null);

  // Mutations Apollo
  const [createClient] = useMutation(CREATE_CLIENT);
  const [createPackageClient] = useMutation(CREATE_PACKAGE_CLIENT);
  const [updatePackageClient] = useMutation(UPDATE_PACKAGE_CLIENT);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const agencyId = user.agencyId || 0;
    
    setFormData(prev => ({ 
      ...prev, 
      agencyId 
    }));

    if (existingClient) {
      setFormData({
        firstName: existingClient.firstName,
        lastName: existingClient.lastName,
        email: existingClient.email,
        phone: existingClient.phone,
        agencyId: existingClient.agencyId || agencyId
      });
    }
  }, [existingClient]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setPassportFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (existingPackageClient?.id) {
        // Mode modification
        await updatePackageClient({
          variables: {
            id: existingPackageClient.id,
            input: {
              packageId,
              clientId: existingClient?.id,
              passport: passportFile ? 'path/to/passport' : existingPackageClient.passport
            }
          }
        });
      } else {
        // Mode création
        let clientId = existingClient?.id;

        if (!clientId) {
          const { data } = await createClient({
            variables: {
              input: {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                agencyId: formData.agencyId
              }
            }
          });
          clientId = data.createClient.id;
        }

        if (clientId) {
          await createPackageClient({
            variables: {
              input: {
                packageId,
                clientId,
                passport: passportFile ? 'path/to/passport' : null
              }
            }
          });
        } else {
          throw new Error('Impossible de créer ou récupérer le client');
        }
      }

      onClose();
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      alert('Une erreur est survenue');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {existingClient ? 'Modifier le client' : 'Ajouter un client'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Prénom
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className="w-full p-2 border rounded-md"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Nom
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className="w-full p-2 border rounded-md"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-2 border rounded-md"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Téléphone
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full p-2 border rounded-md"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Passport
            </label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="w-full p-2 border rounded-md"
            />
          </div>

          <div className="flex justify-end gap-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {existingClient ? 'Modifier' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 